import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import AdsBanner from '@/components/feature/AdsBanner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { getOrCreateConversation } from '@/lib/messages';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { mockListings, type Listing } from '@/mocks/listings';

interface ListingDbRow {
  id: string;
  owner_id: string;
  type: string;
  title: string;
  description: string | null;
  price: number | null;
  category_id: string | null;
  city: string | null;
  images: string[] | null;
  files: string[] | null;
  contact_name: string | null;
  contact_phone: string | null;
  created_at: string | null;
}

interface SellerProfileRow {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  phone: string | null;
  verified: boolean;
}

const categories = ['Tous', 'Électronique', 'Services', 'Emploi', 'Sport', 'Éducation', 'Immobilier', 'Autre'];

function phoneDigits(phone: string) {
  return phone.replace(/\D/g, '');
}

function initialsFor(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function Listings() {
  const [listings, setListings] = useState<Listing[]>(mockListings);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('Tous');
  const [search, setSearch] = useState('');
  const [categoryNames, setCategoryNames] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const { user } = useAuth();
  const { copy } = useCopyToClipboard();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [messageLoading, setMessageLoading] = useState<string | null>(null);
  const [messageError, setMessageError] = useState('');
  const shareTimerRef = useRef<number | null>(null);

  const handleShare = (listing: Listing) => {
    const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
    const prefix = basePath ? `/${basePath}` : '';
    const url = `${window.location.origin}${prefix}/annonces`;
    copy(`${listing.title} — ${url}`);
    setCopiedId(listing.id);
    if (shareTimerRef.current) window.clearTimeout(shareTimerRef.current);
    shareTimerRef.current = window.setTimeout(() => setCopiedId(null), 2000);
  };

  const handleMessage = async (listing: Listing) => {
    if (!user) {
      navigate('/auth/login', { state: { from: { pathname: '/annonces' } } });
      return;
    }
    if (user.id === listing.ownerId) return;
    setMessageLoading(listing.id);
    setMessageError('');
    try {
      const conv = await getOrCreateConversation(user.id, listing.ownerId);
      navigate('/messages', { state: { openId: conv.id } });
    } catch {
      setMessageError('Impossible de démarrer la conversation pour le moment.');
      window.setTimeout(() => setMessageError(''), 3500);
    } finally {
      setMessageLoading(null);
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const { data: catData } = await supabase.from('categories').select('id, name');
      if (mounted && catData) {
        const map: Record<string, string> = {};
        (catData as { id: string; name: string }[]).forEach((c) => {
          map[c.id] = c.name;
        });
        setCategoryNames(map);
      }

      const { data, error } = await supabase
        .from('listings')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (mounted && data && data.length > 0 && !error) {
        const rows = data as ListingDbRow[];
        const ownerIds = [...new Set(rows.map((r) => r.owner_id))];
        const { data: profileData } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url, phone, verified')
          .in('id', ownerIds);
        const profileMap: Record<string, SellerProfileRow> = {};
        (profileData || []).forEach((p) => {
          profileMap[(p as SellerProfileRow).id] = p as SellerProfileRow;
        });

        const mapped: Listing[] = rows.map((row) => {
          const seller = profileMap[row.owner_id];
          return {
            id: row.id,
            type: row.type === 'produit' ? 'produit' : 'annonce',
            title: row.title,
            description: row.description || '',
            price: row.price,
            category: row.category_id ? categoryNames[row.category_id] || 'Autre' : 'Autre',
            city: row.city || 'Lomé',
            image: row.images && row.images.length > 0 ? row.images[0] : '',
            ownerId: row.owner_id,
            sellerName: seller?.full_name || row.contact_name || 'Vendeur',
            sellerAvatar: seller?.avatar_url || '',
            sellerPhone: seller?.phone || row.contact_phone || '',
            verified: Boolean(seller?.verified),
            postedAt: row.created_at ? row.created_at.slice(0, 10) : '',
          };
        });
        setListings(mapped);
      }
      if (mounted) setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = listings.filter((l) => {
    const matchesFilter = filter === 'Tous' || l.category === filter;
    const q = search.toLowerCase();
    const matchesSearch =
      !q || l.title.toLowerCase().includes(q) || l.description.toLowerCase().includes(q);
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16 text-center">
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground-50">
              Annonces &amp; Produits
            </h1>
            <p className="text-foreground-400 mt-3 max-w-2xl mx-auto">
              Découvrez les annonces, offres et produits publiés par la communauté WorldApeiron,
              et contactez directement les vendeurs.
            </p>

            <div className="mt-8 max-w-xl mx-auto">
              <div className="flex items-center bg-background-50 border border-background-300/60 rounded-full overflow-hidden focus-within:border-primary-400">
                <div className="w-11 h-11 flex items-center justify-center shrink-0">
                  <i className="ri-search-line text-foreground-400" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher une annonce, un produit..."
                  className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-700 py-3 pr-4 outline-none"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Pub */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
          <AdsBanner pageKey="listings" />
        </section>

        {/* Filters */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex gap-2 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  filter === cat
                    ? 'bg-primary-500 text-foreground-950'
                    : 'bg-background-100 text-foreground-500 border border-background-200/70 hover:border-primary-400/40'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* Listings grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
                <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-primary-500/10">
                <i className="ri-search-line text-3xl text-primary-400" />
              </div>
              <p className="text-foreground-400">Aucune annonce ne correspond à votre recherche.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((listing) => (
                <article
                  key={listing.id}
                  className="rounded-xl bg-background-100 border border-background-200/70 overflow-hidden flex flex-col"
                >
                  <div className="h-44 w-full overflow-hidden">
                    {listing.image ? (
                      <img src={listing.image} alt={listing.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-background-200/60 flex items-center justify-center">
                        <i className="ri-image-line text-3xl text-foreground-400" />
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-background-200/70 text-foreground-500 whitespace-nowrap">
                        {listing.type === 'produit' ? 'Produit' : 'Annonce'}
                      </span>
                      {listing.category && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary-500/10 text-secondary-400 whitespace-nowrap">
                          {listing.category}
                        </span>
                      )}
                    </div>
                    <h2 className="font-heading font-semibold text-foreground-50 leading-snug">
                      {listing.title}
                    </h2>
                    <p className="text-sm text-foreground-400 mt-2 line-clamp-2 flex-1">
                      {listing.description}
                    </p>

                    {/* Vendeur */}
                    <div className="flex items-center gap-3 mt-4 pt-3 border-t border-background-200/70">
                      <div className="w-10 h-10 rounded-full bg-background-200/70 overflow-hidden shrink-0 flex items-center justify-center">
                        {listing.sellerAvatar ? (
                          <img
                            src={listing.sellerAvatar}
                            alt={listing.sellerName}
                            title={`${listing.sellerName} - vendeur`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-sm font-semibold text-primary-400">
                            {initialsFor(listing.sellerName)}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-foreground-50 truncate">
                            {listing.sellerName}
                          </span>
                          {listing.verified && (
                            <i
                              className="ri-verified-badge-fill text-accent-400 text-base shrink-0"
                              title="Vendeur vérifié"
                            />
                          )}
                        </div>
                        <span className="text-xs text-foreground-400">Vendeur</span>
                      </div>
                      {!(user && user.id === listing.ownerId) && (
                        <button
                          type="button"
                          onClick={() => handleMessage(listing)}
                          disabled={messageLoading === listing.id}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-foreground-950 text-xs font-semibold hover:bg-primary-400 transition-colors whitespace-nowrap disabled:opacity-60"
                          aria-label={`Envoyer un message à ${listing.sellerName}`}
                          title="Envoyer un message"
                        >
                          {messageLoading === listing.id ? (
                            <i className="ri-loader-4-line animate-spin" />
                          ) : (
                            <i className="ri-chat-3-line" />
                          )}
                          Message
                        </button>
                      )}
                    </div>

                    {/* Prix + actions */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
                      <span className="font-semibold text-primary-400">
                        {listing.price ? `${listing.price.toLocaleString('fr-FR')} FCFA` : 'Gratuit'}
                      </span>
                      <div className="flex items-center gap-2">
                        {listing.sellerPhone && (
                          <>
                            <a
                              href={`tel:${listing.sellerPhone.replace(/\s/g, '')}`}
                              className="w-9 h-9 flex items-center justify-center rounded-full border border-primary-400/40 text-primary-400 hover:bg-primary-500/10 transition-colors"
                              title="Appeler le vendeur"
                              aria-label={`Appeler ${listing.sellerName}`}
                            >
                              <i className="ri-phone-line" />
                            </a>
                            <a
                              href={`https://wa.me/${phoneDigits(listing.sellerPhone)}`}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="w-9 h-9 flex items-center justify-center rounded-full border border-secondary-400/40 text-secondary-300 hover:bg-secondary-500/10 transition-colors"
                              title="Contacter via WhatsApp"
                              aria-label={`WhatsApp ${listing.sellerName}`}
                            >
                              <i className="ri-whatsapp-line" />
                            </a>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => handleShare(listing)}
                          className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300/60 text-foreground-400 hover:text-primary-400 hover:border-primary-400/40 transition-colors"
                          aria-label={`Partager ${listing.title}`}
                          title="Copier le lien"
                        >
                          <i className={copiedId === listing.id ? 'ri-check-line text-accent-400' : 'ri-share-line'} />
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      {messageError && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-red-500 text-white text-sm px-4 py-2.5 rounded-lg">
          {messageError}
        </div>
      )}

      <Footer />
    </div>
  );
}

export default Listings;